import type { Location, Patient, Reference } from '@medplum/fhirtypes'
import {
  ExtendedEncounter,
  type ExtendedEncounterArgs,
} from '#src/models/encounters/encounter.ts'
import { LocationBasedEncounter } from '#src/models/encounters/location-encounter.ts'
import type { QueryTypes } from '#src/search.ts'

export type AdmissionStatus = 'planned' | 'in-progress' | 'finished'

export class AdmissionEncounter extends LocationBasedEncounter {
  constructor(
    args: Omit<ExtendedEncounterArgs, 'class' | 'location'> & {
      location: Reference<Location>
    }
  ) {
    super({
      ...args,
      class: 'HOSPITAL_INPATIENT',
      location: args.location,
    })
  }

  static readonly ADMISSION_STATUS_OPTIONS = [
    {
      value: 'planned' as const,
      label: 'Planned',
      keywords: ['planned'],
    },
    {
      value: 'in-progress' as const,
      label: 'In Progress',
      keywords: ['in progress', 'in-progress', 'progress'],
    },
    {
      value: 'finished' as const,
      label: 'Finished',
      keywords: ['finished', 'complete', 'completed'],
    },
  ]

  static readonly ADMISSION_STATUS_VALUES = [
    'planned',
    'in-progress',
    'finished',
  ] as const

  static isValidStatus(status: string): status is AdmissionStatus {
    return ['planned', 'in-progress', 'finished'].includes(status)
  }

  static readonly BASE_SEARCH_QUERY: QueryTypes = {
    ...ExtendedEncounter.BASE_QUERY_STRUCTURE,
    class: ExtendedEncounter.ENCOUNTER_CLASS.HOSPITAL_INPATIENT,
  }

  static searchByPatient(patient: Reference<Patient>): QueryTypes {
    return {
      ...this.BASE_SEARCH_QUERY,
      subject: patient,
    }
  }

  static byPatientInProgress(patient: Reference<Patient>): QueryTypes {
    return {
      ...this.BASE_SEARCH_QUERY,
      subject: patient,
      status: 'in-progress',
    }
  }
}
